import React from "react";
import { Link } from "react-router-dom";
import MarketingNavbar from "../components/MarketingNavbar";
import { Button } from "../components/ui/button";
import {
  ArrowUpRight,
  Sparkles,
  Lightbulb,
  Hash,
  Zap,
  Gift,
  Upload,
  Scissors,
  Send,
  Instagram,
  CheckCircle2,
  Quote,
} from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen bg-[var(--mp-bg)] text-[var(--mp-ink)] font-body">
      <MarketingNavbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-paper opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--mp-ink)] text-xs font-bold uppercase tracking-[0.2em] bg-white">
                <Sparkles className="h-3.5 w-3.5" /> Social agency per saloni
              </div>
              <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter">
                Social pronto,
                <br />
                <span className="inline-block px-3 -mx-1 bg-[var(--mp-yellow)]">
                  senza fatica
                </span>
                <br />
                per il tuo salone.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-[var(--mp-muted)] leading-relaxed">
                Entra gratis e usa subito strumenti AI per idee, hashtag e
                contenuti. Passa a Pro e facciamo noi il 95% del lavoro: tu
                mandi il grezzo, noi pubblichiamo su tutte le piattaforme.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link to="/cliente/registrati" data-testid="hero-cta-free">
                  <Button className="rounded-full h-12 px-7 text-base bg-[var(--mp-primary)] hover:bg-[var(--mp-primary-hover)] text-white shadow-brutal-sm hover-brutal-lift border border-[var(--mp-ink)]">
                    Entra gratis <ArrowUpRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <a
                  href="https://www.nicobenz.it/prenota"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-consult"
                >
                  <Button
                    variant="outline"
                    className="rounded-full h-12 px-7 text-base bg-white border-[var(--mp-ink)] hover:bg-[var(--mp-yellow)]"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Consulenza gratuita
                  </Button>
                </a>
              </div>
            </div>

            <div
              className="lg:col-span-5 fade-up flex items-center justify-center"
              style={{ animationDelay: "120ms" }}
            >
              <picture>
                <source
                  type="image/webp"
                  srcSet="/img/hero-480.webp 480w, /img/hero-960.webp 960w, /img/hero-1440.webp 1440w"
                  sizes="(max-width: 1023px) 90vw, 720px"
                />
                <source
                  type="image/jpeg"
                  srcSet="/img/hero-480.jpg 480w, /img/hero-960.jpg 960w, /img/hero-1440.jpg 1440w"
                  sizes="(max-width: 1023px) 90vw, 720px"
                />
                <img
                  src="/img/hero-960.jpg"
                  alt="Marketing Parrucchieri — La prima agenzia di marketing online specializzata per il settore della bellezza"
                  data-testid="hero-logo"
                  width="1536"
                  height="1024"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto object-contain"
                  style={{ maxWidth: "720px", transform: "scale(1.25)" }}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[var(--mp-ink)] bg-[var(--mp-ink)] text-white overflow-hidden">
        <div className="whitespace-nowrap py-4 flex marquee">
          {["loop-a", "loop-b"].map((loopKey) => (
            <div
              key={loopKey}
              className="flex items-center gap-10 pr-10 font-display text-2xl"
            >
              <span>Instagram</span>
              <span className="text-[var(--mp-yellow)]">✦</span>
              <span>TikTok</span>
              <span className="text-[var(--mp-primary)]">✦</span>
              <span>Facebook</span>
              <span className="text-[var(--mp-secondary)]">✦</span>
              <span>YouTube</span>
              <span className="text-[var(--mp-yellow)]">✦</span>
              <span>Google Business</span>
              <span className="text-[var(--mp-primary)]">✦</span>
            </div>
          ))}
        </div>
      </section>

      {/* FREE SECTION */}
      <section
        id="gratis"
        className="max-w-7xl mx-auto px-6 sm:px-10 py-20"
        data-testid="home-free-section"
      >
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-2 border-[var(--mp-ink)] text-sm sm:text-base font-bold uppercase tracking-[0.2em] shadow-brutal-sm">
              <span className="font-display text-lg sm:text-xl">01</span>
              <span>— Gratis</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-4">
              Entra e usa subito{" "}
              <span className="inline-block bg-[var(--mp-yellow)] px-2">
                strumenti AI
              </span>
              .
            </h2>
          </div>
          <p className="max-w-md text-[var(--mp-muted)]">
            Zero carta di credito, zero abbonamenti. Registrati, accedi e sei
            già dentro.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <FreeCard
            tone="yellow"
            icon={<Lightbulb className="h-5 w-5" />}
            title="Idee video AI"
            text="Ogni giorno nuove idee di contenuto pensate per il tuo salone e il tuo posizionamento."
          />
          <FreeCard
            tone="paper"
            icon={<Hash className="h-5 w-5" />}
            title="Generatore hashtag"
            text="Hashtag ottimizzati per città + servizio. Geo, brand, nicchia — divisi e pronti da copiare."
          />
          <FreeCard
            tone="yellow"
            icon={<Gift className="h-5 w-5" />}
            title="Generatore offerte"
            text="Crea offerte promozionali irresistibili in 30 secondi: nome offerta, testo, messaggio WhatsApp e stories, pronti da copiare."
          />
          <FreeCard
            tone="ink"
            icon={<Zap className="h-5 w-5" />}
            title="Altri strumenti utili"
            text="Idee per storie, strumenti per i social, guide pratiche — aggiungiamo novità ogni mese."
          />
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <Link to="/cliente/registrati" data-testid="free-cta-register">
            <Button className="rounded-full h-12 px-7 bg-[var(--mp-ink)] hover:bg-black text-white">
              Crea account gratuito
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/cliente/login" className="text-sm underline">
            Ho già un account
          </Link>
        </div>
      </section>

      {/* PRO SECTION */}
      <section
        id="pro"
        className="border-y border-[var(--mp-ink)] bg-[var(--mp-ink)] text-white"
        data-testid="home-pro-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--mp-primary)] text-white border-2 border-white/20 text-sm sm:text-base font-bold uppercase tracking-[0.2em] shadow-brutal-sm">
                <span className="font-display text-lg sm:text-xl">02</span>
                <span>— Pro</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-4">
                Facciamo noi il{" "}
                <span className="inline-block bg-[var(--mp-primary)] px-2 text-white">
                  95% del lavoro
                </span>
                .
              </h2>
              <p className="mt-4 text-white/70 max-w-xl">
                A te resta solo il 5%: mandarci il grezzo. Al resto pensiamo
                noi — dal montaggio alla pubblicazione su tutte le piattaforme.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ProStep
              num="01"
              icon={<Upload className="h-5 w-5" />}
              title="Tu mandi il grezzo"
              text="Video col telefono, foto in salone, idee al volo. Li carichi in 10 secondi dal tuo Telegram."
            />
            <ProStep
              num="02"
              icon={<Scissors className="h-5 w-5" />}
              title="Noi montiamo"
              text="Sottotitoli, musica, tagli degli errori, color grading, formato giusto. Pronto per essere pubblicato."
            />
            <ProStep
              num="03"
              icon={<Send className="h-5 w-5" />}
              title="Pubblichiamo ovunque"
              text="Facebook, TikTok, YouTube, Google Business, Instagram. Tu sei online ogni giorno. Senza pensarci."
              highlight
            />
          </div>

          <div className="mt-12 rounded-3xl border border-white/20 bg-white/5 backdrop-blur p-6 sm:p-8 flex items-center justify-between flex-wrap gap-6">
            <div className="max-w-xl">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-white/60 mb-2">
                Pronto a delegare?
              </div>
              <div className="font-display text-2xl sm:text-3xl tracking-tight">
                Parliamone. Ti chiamiamo noi e vediamo se Pro fa per te.
              </div>
            </div>
            <a
              href="https://www.nicobenz.it/prenota"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pro-cta-consult"
            >
              <Button
                className="rounded-full h-12 px-7 text-base bg-[var(--mp-yellow)] hover:bg-[var(--mp-yellow)] text-[var(--mp-ink)] border border-[var(--mp-ink)] font-bold"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Richiedi consulenza gratuita
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3 text-white/70 text-sm">
            <Badge label="FB" />
            <Badge label="Instagram" icon={<Instagram className="h-3 w-3" />} />
            <Badge label="TikTok" />
            <Badge label="YouTube" />
            <Badge label="Google" />
            <CheckCircle2 className="h-4 w-4 text-[var(--mp-yellow)]" />
            <span>Cross-posting automatico</span>
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION */}
      <section
        id="founder"
        className="max-w-7xl mx-auto px-6 sm:px-10 py-24"
        data-testid="home-founder-section"
      >
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden border border-[var(--mp-ink)] shadow-brutal">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/img/founder-480.webp 480w, /img/founder-800.webp 800w, /img/founder-1200.webp 1200w"
                  sizes="(max-width: 1023px) 90vw, 480px"
                />
                <source
                  type="image/jpeg"
                  srcSet="/img/founder-480.jpg 480w, /img/founder-800.jpg 800w, /img/founder-1200.jpg 1200w"
                  sizes="(max-width: 1023px) 90vw, 480px"
                />
                <img
                  src="/img/founder-800.jpg"
                  alt="Nico Benz, founder Marketing Parrucchieri"
                  width="1024"
                  height="1536"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-[520px] object-cover"
                />
              </picture>
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-[var(--mp-yellow)] backdrop-blur px-4 py-3 border border-[var(--mp-ink)]">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--mp-ink)]/70">
                  Founder
                </div>
                <div className="font-display text-xl tracking-tight">
                  Nico Benz
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              03 — Chi c'è dietro
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mt-2">
              Da un'idea di{" "}
              <span className="inline-block bg-[var(--mp-yellow)] px-2">
                Nico Benz
              </span>
              .
            </h2>
            <div className="mt-6 relative pl-6 border-l-4 border-[var(--mp-primary)]">
              <Quote className="absolute -left-3 top-0 h-6 w-6 bg-[var(--mp-bg)] text-[var(--mp-primary)]" />
              <p className="font-display text-xl sm:text-2xl tracking-tight leading-snug">
                "I parrucchieri devono stare col cliente in poltrona, non
                davanti al computer a montare video. Noi creiamo la macchina;
                tu continui a fare quello che sai fare meglio."
              </p>
            </div>
            <p className="mt-6 text-[var(--mp-muted)] max-w-xl leading-relaxed">
              Dopo anni nel mondo dei social e del personal branding, Nico Benz
              ha fondato <strong>Marketing Parrucchieri SRL</strong> con
              un'idea semplice: dare ai saloni di bellezza una presenza online
              professionale senza chiedergli di diventare videomaker.
              Contenuti autentici, strategia di brand, pubblicazione su tutte
              le piattaforme. Tutto gestito da noi.
            </p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <ValueCard
                title="Contenuto autentico"
                text="Il tuo salone, la tua voce, i tuoi clienti veri."
              />
              <ValueCard
                title="Personal brand"
                text="Non un logo. Una persona riconoscibile."
              />
              <ValueCard
                title="Zero sbatti"
                text="Tu tagli capelli. Noi tagliamo video."
              />
            </div>

            <div className="mt-8">
              <a
                href="https://www.nicobenz.it"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-cta-website"
              >
                <Button className="rounded-full h-12 px-7 text-base bg-[var(--mp-ink)] hover:bg-black text-white shadow-brutal-sm hover-brutal-lift border border-[var(--mp-ink)]">
                  Scopri Nico Benz
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS — Agenzia & Cliente */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-6">
        <div
          id="agenzia"
          data-testid="area-agenzia"
          className="relative overflow-hidden rounded-3xl border border-[var(--mp-ink)] bg-white p-8 sm:p-10 shadow-brutal hover-brutal-lift"
        >
          <img
            src="https://images.pexels.com/photos/6476191/pexels-photo-6476191.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Team agenzia"
            loading="lazy"
            decoding="async"
            width="940"
            height="650"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
          />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Area Agenzia
            </div>
            <h3 className="font-display mt-2 text-3xl sm:text-4xl font-medium tracking-tight">
              Collaboratori <br /> Marketing Parrucchieri.
            </h3>
            <p className="mt-4 text-[var(--mp-muted)] max-w-md">
              Accesso riservato al team interno. Le nuove richieste di
              registrazione devono essere approvate dall'amministratore.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/agenzia/login" data-testid="agency-login-link">
                <Button className="rounded-full h-11 px-6 bg-[var(--mp-ink)] hover:bg-black text-white">
                  Accedi
                </Button>
              </Link>
              <Link to="/agenzia/registrati" data-testid="agency-register-link">
                <Button
                  variant="outline"
                  className="rounded-full h-11 px-6 border-[var(--mp-ink)] hover:bg-[var(--mp-yellow)]"
                >
                  Richiedi accesso
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div
          id="cliente"
          data-testid="area-cliente"
          className="relative overflow-hidden rounded-3xl border border-[var(--mp-ink)] bg-[var(--mp-primary)] text-white p-8 sm:p-10 shadow-brutal hover-brutal-lift"
        >
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.22em] opacity-90">
              Area Cliente
            </div>
            <h3 className="font-display mt-2 text-3xl sm:text-4xl font-medium tracking-tight">
              Saloni & parrucchieri <br /> connessi all'agenzia.
            </h3>
            <p className="mt-4 text-white/90 max-w-md">
              Iscriviti con email, dati del salone e referente: entri in
              versione Free o chiedi una consulenza per passare a Pro.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cliente/login" data-testid="client-login-link">
                <Button className="rounded-full h-11 px-6 bg-white text-[var(--mp-ink)] hover:bg-[var(--mp-yellow)]">
                  Accedi
                </Button>
              </Link>
              <Link to="/cliente/registrati" data-testid="client-register-link">
                <Button
                  variant="outline"
                  className="rounded-full h-11 px-6 bg-transparent text-white border-white hover:bg-white hover:text-[var(--mp-ink)]"
                >
                  Crea account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[var(--mp-border)] bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display text-sm">
              © {new Date().getFullYear()} Marketing Parrucchieri SRL
            </div>
            <div className="text-[11px] text-[var(--mp-muted)] mt-1">
              Da un'idea di{" "}
              <strong className="text-[var(--mp-ink)]">Nico Benz</strong>,
              founder.
            </div>
          </div>
          <div className="text-xs text-[var(--mp-muted)] uppercase tracking-[0.2em]">
            Piattaforma clienti & agenzia
          </div>
        </div>
      </footer>
    </div>
  );
}

function FreeCard({ tone, icon, title, text }) {
  const styles = {
    yellow: "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)]",
    ink: "bg-[var(--mp-ink)] text-white border-[var(--mp-ink)]",
    paper: "bg-white text-[var(--mp-ink)] border-[var(--mp-ink)]",
  };
  return (
    <div
      className={`group rounded-3xl border ${styles[tone]} p-6 sm:p-7 min-h-[200px] flex flex-col justify-between hover-brutal-lift shadow-brutal-sm`}
    >
      <div className="h-10 w-10 rounded-full border border-current grid place-items-center">
        {icon}
      </div>
      <div>
        <div className="inline-block px-2 py-0.5 rounded-full border border-current text-[9px] uppercase tracking-[0.2em] font-bold mb-2 opacity-80">
          Gratis
        </div>
        <h4 className="font-display text-xl sm:text-2xl tracking-tight">
          {title}
        </h4>
        <p className="mt-2 text-sm opacity-90">{text}</p>
      </div>
    </div>
  );
}

function ProStep({ num, icon, title, text, highlight }) {
  return (
    <div
      className={`rounded-3xl border p-6 sm:p-7 min-h-[220px] flex flex-col justify-between hover-brutal-lift ${
        highlight
          ? "bg-[var(--mp-yellow)] text-[var(--mp-ink)] border-[var(--mp-ink)] shadow-brutal-sm"
          : "bg-white/5 text-white border-white/20"
      }`}
    >
      <div className="flex items-center justify-between">
        <div
          className={`h-10 w-10 rounded-full border grid place-items-center ${
            highlight ? "border-[var(--mp-ink)]" : "border-white/40"
          }`}
        >
          {icon}
        </div>
        <div
          className={`font-display text-3xl ${
            highlight ? "opacity-70" : "text-white/40"
          }`}
        >
          {num}
        </div>
      </div>
      <div>
        <h4 className="font-display text-xl sm:text-2xl tracking-tight">
          {title}
        </h4>
        <p
          className={`mt-2 text-sm ${
            highlight ? "opacity-80" : "text-white/70"
          }`}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

function Badge({ label, icon }) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-white/30 text-[11px] uppercase tracking-[0.18em] font-bold">
      {icon}
      {label}
    </span>
  );
}

function ValueCard({ title, text }) {
  return (
    <div className="rounded-2xl border border-[var(--mp-ink)] bg-white p-4 shadow-brutal-sm">
      <div className="font-display text-sm sm:text-base tracking-tight">
        {title}
      </div>
      <div className="text-[12px] text-[var(--mp-muted)] mt-1 leading-relaxed">
        {text}
      </div>
    </div>
  );
}
